<template>
  <div class="ttd-digital-detail-page">
    <!-- Loading State -->
    <div v-if="pageLoading" class="loading-container">
      <a-spin size="large" tip="Memuat data surat..." />
    </div>

    <template v-else>
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-title-wrapper">
          <h1>Detail Surat Keluar</h1>
          <div class="status-surat">
            <a-tag :color="getStatusColor(displayStatus)">
              <strong>{{ getStatusText(displayStatus) }}</strong>
            </a-tag>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="content-container">
        <h4 class="section-title">Informasi Surat</h4>

        <!-- Info Fields using a-row like UpdateSuratMasuk -->
        <div class="info-fields">
          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Pengaju Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">
                {{ suratData.creator?.name || '-' }} ({{
                  suratData.creator?.jabatan || 'Staf Sekretariat'
                }})
              </span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Waktu Dibuat/Unggah</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ formatDateTime(suratData.createdAt) }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Jenis Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData.jenisSurat || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Nomor Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData.nomorSurat || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Sifat Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData.sifatSurat || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Bentuk Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ getBentukSuratDescription(suratData.bentukSurat) }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Penandatangan 1</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{
                formatPenandatangan(suratData.penandatanganKiri, 'kiri')
              }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Penandatangan 2</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{
                formatPenandatangan(suratData.penandatanganKanan, 'kanan')
              }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Verifikator</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ formatVerifikatorList(suratData.verifikator) }}</span>
            </a-col>
          </a-row>

          <a-row v-if="suratData.suratUndanganAcara" :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Surat Undangan Acara</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">
                <a target="_blank" class="file-link" @click="openSuratMasukPreview">
                  <PaperClipOutlined />
                  {{
                    suratData.suratUndanganAcara.perihal || suratData.suratUndanganAcara.nomorSurat
                  }}
                </a>
                <a-button type="text" class="btn-preview-small" @click="openSuratMasukPreview">
                  <EyeOutlined />
                </a-button>
                <a-button type="text" class="btn-external-small" @click="openSuratMasukNewTab">
                  <ExportOutlined />
                </a-button>
              </span>
            </a-col>
          </a-row>
        </div>

        <div v-if="needsResignAlert" class="resign-alert-section">
          <a-alert class="custom-pink-alert" :show-icon="false">
            <template #message>
              <span class="alert-title">Terjadi Perubahan Pada Surat Ini</span>
            </template>
            <template #description>
              <p class="alert-desc">
                Anda sudah pernah menandatangani surat ini. Namun, terdapat permintaan revisi dari
                salah satu penandatangan sehingga terjadi perubahan isi surat.
                <br />
                Mohon tandatangani surat ini kembali.
              </p>
            </template>
          </a-alert>
        </div>

        <!-- Alert: User Belum Atur Tanda Tangan -->
        <div v-if="canTakeAction && !userHasSignature" class="signature-warning-section">
          <a-alert type="warning" :show-icon="true" class="custom-signature-alert">
            <template #message>
              <span class="alert-title">Tanda Tangan Belum Diatur</span>
            </template>
            <template #description>
              <p class="alert-desc">
                Anda belum mengatur tanda tangan digital. Silakan atur tanda tangan Anda terlebih
                dahulu di menu <strong>Pengaturan Surat > Tanda Tangan</strong> sebelum
                menandatangani surat ini.
                <br />
                Anda masih dapat mengajukan permintaan revisi.
              </p>
            </template>
          </a-alert>
        </div>

        <!-- Detail Revisi Section (only show penandatangan revisions) -->
        <DetailRevisi
          :verifikator="suratData.verifikator"
          :penandatangan="suratData.penandatangan"
          show-only="penandatangan"
        />

        <!-- Dokumen Surat Section -->
        <div class="dokumen-section">
          <h4 class="section-title">{{ isSuratTugas ? 'Surat Tugas' : 'Dokumen Surat' }}</h4>
          <SuratTugasViewer
            :surat-tugas-list="suratTugasList"
            :single-pdf-path="!isSuratTugas || suratTugasList.length === 0 ? suratData.pdfPath : ''"
            :nomor-surat="suratData.nomorSurat"
            :empty-message="
              isSuratTugas ? 'Belum ada surat tugas yang dibuat' : 'PDF surat belum tersedia'
            "
          />
        </div>

        <!-- Action Buttons -->
        <div class="action-section" v-if="canTakeAction">
          <!-- Confirmation Checkbox - Only show if user has signature -->
          <div class="confirmation-checkbox" v-if="userHasSignature">
            <a-checkbox v-model:checked="isConfirmed">
              <span class="checkbox-label">
                Saya telah memastikan bahwa isi surat adalah benar dan saya setuju untuk
                menandatangani surat ini.
              </span>
            </a-checkbox>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <!-- Tandatangani button - Only show if user has signature -->
            <a-button
              v-if="userHasSignature"
              class="btn-pill-red"
              @click="handleSign"
              :loading="signLoading"
              :disabled="!isConfirmed || signLoading"
            >
              TANDA TANGANI SURAT
            </a-button>
            <a-button class="btn-pill-black" @click="showReviseModal" :disabled="signLoading">
              MINTA REVISI
            </a-button>
          </div>
        </div>
      </div>
    </template>

    <!-- Revise Modal -->
    <a-modal
      v-model:visible="reviseModalVisible"
      :footer="null"
      :closable="true"
      width="600px"
      centered
    >
      <div class="modal-confirm">
        <h3 class="modal-title">Minta sekretaris merevisi surat?</h3>
        <p class="modal-description">
          Surat akan dikembalikan ke pembuat surat untuk direvisi kembali. Mohon masukkan catatan
          hal yang perlu direvisi pada surat.
        </p>
        <a-form layout="vertical">
          <a-form-item label="Catatan Revisi" required class="form-item-revisi">
            <a-textarea
              v-model:value="reviseCatatan"
              placeholder="Masukkan catatan revisi yang perlu diperbaiki pengaju surat"
              :rows="4"
            />
          </a-form-item>
        </a-form>
        <div class="modal-actions">
          <a-button class="btn-cancel" @click="handleCancelRevise" :disabled="reviseLoading">
            KEMBALI
          </a-button>
          <a-button class="btn-confirm" @click="handleRevise" :loading="reviseLoading">
            YA, SIMPAN
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- PDF Preview Modal for Surat Masuk -->
    <PDFPreviewModal
      v-model:visible="pdfPreviewVisible"
      :pdf-path="pdfPreviewPath"
      :title="pdfPreviewTitle"
      :show-download="true"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { notification, Alert } from 'ant-design-vue'
import { PaperClipOutlined, EyeOutlined, ExportOutlined } from '@ant-design/icons-vue'
import { penandatangananAPI, suratKeluarAPI, getFileUrl } from '@/services/axios/perki'
import { SuratTugasViewer, DetailRevisi, PDFPreviewModal } from '@/components/SuratKeluar'
import moment from 'moment'
import 'moment/locale/id'

moment.locale('id')

export default defineComponent({
  name: 'TtdDigitalDetail',
  components: {
    'a-alert': Alert,
    PaperClipOutlined,
    EyeOutlined,
    ExportOutlined,
    SuratTugasViewer,
    DetailRevisi,
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const pageLoading = ref(true)
    const signLoading = ref(false)
    const reviseLoading = ref(false)
    const suratData = ref({})
    const suratTugasList = ref([])

    // Confirmation checkbox
    const isConfirmed = ref(false)

    // Current user's penandatangan entry
    const currentPenandatanganEntry = ref(null)

    // Revise modal
    const reviseModalVisible = ref(false)
    const reviseCatatan = ref('')

    // PDF Preview modal (for Surat Masuk preview)
    const pdfPreviewVisible = ref(false)
    const pdfPreviewPath = ref('')
    const pdfPreviewTitle = ref('')

    // Get current user ID from localStorage
    const currentUserId = computed(() => {
      try {
        const userData = localStorage.getItem('perki_user')
        if (userData) {
          const user = JSON.parse(userData)
          return user.id
        }
      } catch (e) {
        console.error('Error parsing user data:', e)
      }
      return null
    })

    // Check if this is Surat Tugas type
    const isSuratTugas = computed(() => {
      return suratData.value.jenisSurat === 'Surat Tugas'
    })

    // Check if user needs to re-sign due to content change
    const needsResignAlert = computed(() => {
      // Must be in Penandatanganan status
      if (suratData.value.status !== 'Penandatanganan') return false

      // Must have a penandatangan entry for current user
      if (!currentPenandatanganEntry.value) return false

      // Check if needsResignDueToChange flag is set and there's a previous signature
      return (
        currentPenandatanganEntry.value.needsResignDueToChange === true &&
        currentPenandatanganEntry.value.previouslySignedAt != null
      )
    })

    // Check if current user has signature configured
    const userHasSignature = computed(() => {
      // Find current user from penandatangan list
      if (!suratData.value.penandatangan) return false

      const currentUserPenandatangan = suratData.value.penandatangan.find(
        p => p.userId === currentUserId.value,
      )

      if (!currentUserPenandatangan || !currentUserPenandatangan.user) return false

      // Check if user has signaturePath
      return !!(
        currentUserPenandatangan.user.signaturePath &&
        currentUserPenandatangan.user.signaturePath.trim()
      )
    })

    // Check if user can take action (is the current penandatangan with pending status)
    const canTakeAction = computed(() => {
      // Must be in Penandatanganan status
      if (suratData.value.status !== 'Penandatanganan') return false

      // Must have a penandatangan entry for current user
      if (!currentPenandatanganEntry.value) return false

      // User must have pending status
      if (currentPenandatanganEntry.value.status !== 'pending') return false

      return true
    })

    // Compute the penandatanganan status for display
    const displayStatus = computed(() => {
      // Check if current user is a penandatangan
      if (currentPenandatanganEntry.value) {
        const status = currentPenandatanganEntry.value.status
        if (status === 'pending') return 'Butuh TTD'
        if (status === 'signed') return 'Selesai'
        if (status === 'revised') return 'Revisi'
      }

      // Fallback to surat status
      const suratStatus = suratData.value.status
      if (suratStatus === 'Penandatanganan') return 'Menunggu'
      if (suratStatus === 'Terkirim') return 'Selesai'
      return suratStatus || '-'
    })

    // Format datetime
    const formatDateTime = dateStr => {
      if (!dateStr) return '-'
      return moment(dateStr).format('dddd YYYY-MM-DD HH:mm')
    }

    // Get status color
    const getStatusColor = status => {
      const colors = {
        pending: 'orange',
        approved: 'green',
        rejected: 'red',
        revised: 'red',
        signed: 'green',
        'Butuh TTD': 'orange',
        Selesai: 'green',
        Dibatalkan: 'default',
        Revisi: 'red',
        Menunggu: 'blue',
        Penandatanganan: 'blue',
      }
      return colors[status] || 'default'
    }

    // Get status text
    const getStatusText = status => {
      const texts = {
        pending: 'Butuh TTD',
        approved: 'Disetujui',
        rejected: 'Ditolak',
        revised: 'Revisi',
        signed: 'Selesai',
        'Butuh TTD': 'Butuh TTD',
        Selesai: 'Selesai',
        Dibatalkan: 'Dibatalkan',
        Revisi: 'Revisi',
        Menunggu: 'Menunggu',
        Penandatanganan: 'Penandatanganan',
      }
      return texts[status] || status || 'Pending'
    }

    // Get bentuk surat description
    const getBentukSuratDescription = bentuk => {
      if (bentuk === 'Soft copy') {
        return 'Soft copy (Menggunakan tanda tangan dan stempel digital. Surat dapat diunduh di website ini (format PDF).)'
      }
      return bentuk || '-'
    }

    // Format penandatangan
    const formatPenandatangan = (penandatangan, position) => {
      if (!penandatangan) return 'Tidak ada'
      const name = penandatangan.name || 'Unknown'
      const jabatan = penandatangan.jabatan || ''
      return jabatan ? `${name}, ${jabatan}` : name
    }

    // Format verifikator list
    const formatVerifikatorList = verifikator => {
      if (!verifikator || verifikator.length === 0) return '-'

      // Sort by urutan first (ascending)
      const sortedVerifikator = [...verifikator].sort((a, b) => {
        const urutanA = a.urutan || 0
        const urutanB = b.urutan || 0
        return urutanA - urutanB
      })

      return sortedVerifikator
        .map((v, index) => {
          const name = v.user?.name || 'Unknown'
          let statusText = ''

          if (v.status === 'approved') {
            statusText = '(Setuju)'
          } else if (v.status === 'revised' || v.status === 'revision_requested') {
            statusText = '(Revisi)'
          } else if (v.status === 'pending') {
            // Check if all previous verifikators are approved
            const allPreviousApproved = sortedVerifikator
              .slice(0, index)
              .every(prev => prev.status === 'approved')

            if (allPreviousApproved) {
              statusText = '(Sedang Verifikasi)'
            } else {
              statusText = '(Menunggu)'
            }
          }
          return `${name} ${statusText}`
        })
        .join(' → ')
    }

    // Fetch surat detail
    const fetchData = async () => {
      pageLoading.value = true
      try {
        const id = route.params.id
        const response = await suratKeluarAPI.getById(id)

        if (response.data?.data) {
          suratData.value = response.data.data

          // Find current user's penandatangan entry
          if (suratData.value.penandatangan && suratData.value.penandatangan.length > 0) {
            const userId = currentUserId.value
            currentPenandatanganEntry.value = suratData.value.penandatangan.find(
              p => p.userId === userId,
            )
          }

          // Build surat tugas list from perwakilan dinas
          if (suratData.value.perwakilanDinas && suratData.value.perwakilanDinas.length > 0) {
            suratTugasList.value = suratData.value.perwakilanDinas
              .filter(p => p.pdfPath)
              .map(p => ({
                id: p.id,
                pdfPath: p.pdfPath,
                perwakilan: p,
                fileName: `Surat Tugas ${suratData.value.nomorSurat}`,
              }))
          }
        }
      } catch (error) {
        console.error('Error fetching surat detail:', error)
        notification.error({ message: 'Gagal memuat data surat' })
      } finally {
        pageLoading.value = false
      }
    }

    // Handle sign
    const handleSign = async () => {
      signLoading.value = true
      try {
        const id = route.params.id
        await penandatangananAPI.sign(id, {})
        notification.success({ message: 'Surat berhasil ditandatangani' })
        router.push('/ttd-digital')
      } catch (error) {
        console.error('Error signing surat:', error)
        notification.error({
          message: error.response?.data?.message || 'Gagal menandatangani surat',
        })
      } finally {
        signLoading.value = false
      }
    }

    // Show revise modal
    const showReviseModal = () => {
      reviseCatatan.value = ''
      reviseModalVisible.value = true
    }

    // Handle cancel revise
    const handleCancelRevise = () => {
      reviseModalVisible.value = false
      reviseCatatan.value = ''
    }

    // Handle revise
    const handleRevise = async () => {
      if (!reviseCatatan.value.trim()) {
        notification.error({
          message: 'Error',
          description: 'Catatan revisi harus diisi',
        })
        return
      }

      reviseLoading.value = true
      try {
        const id = route.params.id
        await penandatangananAPI.revise(id, { catatanRevisi: reviseCatatan.value })
        notification.success({
          message: 'Berhasil',
          description: 'Permintaan revisi berhasil dikirim',
        })
        reviseModalVisible.value = false
        reviseCatatan.value = ''
        setTimeout(() => {
          router.push('/ttd-digital')
        }, 500)
      } catch (error) {
        console.error('Error requesting revise:', error)
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Gagal mengirim permintaan revisi',
        })
      } finally {
        reviseLoading.value = false
      }
    }

    // Open surat masuk preview
    const openSuratMasukPreview = () => {
      if (suratData.value.suratUndanganAcara?.pathToFile) {
        pdfPreviewPath.value = suratData.value.suratUndanganAcara.pathToFile
        pdfPreviewTitle.value =
          suratData.value.suratUndanganAcara.perihal ||
          suratData.value.suratUndanganAcara.nomorSurat ||
          'Surat Masuk'
        pdfPreviewVisible.value = true
      }
    }

    // Open surat masuk in new tab (kept for backward compatibility, now opens modal)
    const openSuratMasukNewTab = () => {
      if (suratData.value.suratUndanganAcara?.pathToFile) {
        window.open(getFileUrl(suratData.value.suratUndanganAcara.pathToFile), '_blank')
      }
    }

    // Get surat masuk file name from path
    const getSuratMasukFileName = pathToFile => {
      if (!pathToFile) return ''
      const parts = pathToFile.split('/')
      const fileNameWithPrefix = parts[parts.length - 1]
      // Remove number prefix like "2_" from filename
      const withoutPrefix = fileNameWithPrefix.replace(/^\d+_/, '')
      return withoutPrefix
    }

    onMounted(() => {
      fetchData()
    })

    return {
      pageLoading,
      signLoading,
      reviseLoading,
      suratData,
      suratTugasList,
      currentPenandatanganEntry,
      isConfirmed,
      isSuratTugas,
      needsResignAlert,
      userHasSignature,
      canTakeAction,
      displayStatus,
      reviseModalVisible,
      reviseCatatan,
      pdfPreviewVisible,
      pdfPreviewPath,
      pdfPreviewTitle,
      formatDateTime,
      getStatusColor,
      getBentukSuratDescription,
      formatPenandatangan,
      formatVerifikatorList,
      getFileUrl,
      handleSign,
      showReviseModal,
      handleCancelRevise,
      handleRevise,
      openSuratMasukPreview,
      openSuratMasukNewTab,
      getStatusText,
    }
  },
})
</script>

<style lang="scss" scoped>
.ttd-digital-detail-page {
  min-height: calc(100vh - 64px);
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  .page-header {
    margin-bottom: 24px;

    .header-title-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #262626;
    }

    .status-surat {
      display: flex;
      gap: 8px;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: white;
    border-radius: 8px;
    padding: 48px 24px;
  }

  .content-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      padding: 16px;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #262626;
      margin: 0 0 16px 0;
      padding-bottom: 12px;
      border-bottom: 1px solid #f0f0f0;
    }

    .info-fields {
      margin-bottom: 24px;

      .info-row {
        padding: 10px 0;
        border-bottom: 1px solid #f5f5f5;

        &:last-child {
          border-bottom: none;
        }

        .info-label {
          font-weight: 500;
          color: #595959;
        }

        .info-value {
          color: #262626;

          .file-link {
            color: #1890ff;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }

          .btn-preview-small,
          .btn-external-small {
            color: #e10f28;
            padding: 2px 6px;
            margin-left: 8px;

            &:hover {
              color: #c41a3d;
              background: transparent;
            }
          }
        }
      }
    }

    .dokumen-section {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #f0f0f0;

      .section-title {
        border-bottom: none;
        padding-bottom: 0;
      }
    }

    .resign-alert-section {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #f0f0f0;

      .custom-pink-alert {
        /* Background color similar to the image (very light pink) */
        background-color: #fff5f7;
        border: none; /* Remove default border */
        border-radius: 16px; /* High border radius for rounded look */
        padding: 24px 32px; /* Generous padding */

        /* Style the Title */
        :deep(.ant-alert-message) {
          margin-bottom: 12px;
        }

        .alert-title {
          font-size: 18px;
          font-weight: 700;
          color: #262626;
        }

        /* Style the Description */
        .alert-desc {
          font-size: 14px;
          color: #595959;
          line-height: 1.6;
          margin: 0;
        }
      }
    }

    .signature-warning-section {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #f0f0f0;

      .custom-signature-alert {
        border-radius: 12px;

        .alert-title {
          font-size: 16px;
          font-weight: 700;
          color: #262626;
        }

        .alert-desc {
          font-size: 14px;
          color: #595959;
          line-height: 1.6;
          margin: 0;

          strong {
            font-weight: 600;
            color: #262626;
          }
        }
      }
    }

    .action-section {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #f0f0f0;

      @media (max-width: 768px) {
        margin-top: 16px;
        padding-top: 16px;
      }

      .confirmation-checkbox {
        display: block;
        margin-top: 0px;
        margin-bottom: 16px;

        @media (max-width: 768px) {
          margin-bottom: 12px;
        }

        .checkbox-label {
          font-size: 14px;
          font-weight: 500;
          color: #262626;
          margin-left: 8px;

          @media (max-width: 576px) {
            font-size: 13px;
          }
        }

        :deep(.ant-checkbox-checked .ant-checkbox-inner) {
          background-color: #e10f28;
          border-color: #e10f28;
        }

        :deep(.ant-checkbox-wrapper:hover .ant-checkbox-inner) {
          border-color: #e10f28;
        }
      }

      .action-buttons {
        display: flex;
        gap: 16px;

        @media (max-width: 768px) {
          flex-direction: column;
          gap: 12px;
        }

        .btn-pill-red,
        .btn-pill-black {
          flex: 1;
          max-width: 250px;

          @media (max-width: 768px) {
            max-width: 100%;
            width: 100%;
          }
        }
      }
    }
  }
}

// Button Pill Styles
.btn-pill-black {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #262626;
  background: white;
  color: #262626;
  padding: 6px 16px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover:not(:disabled) {
    background: #262626 !important;
    border-color: #262626 !important;
    color: white !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }
}

.btn-pill-red {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  background: #e10f28;
  color: white;
  padding: 6px 16px;
  min-height: auto;
  height: 42px;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover:not(:disabled) {
    background: white !important;
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }

  /* Add !important here to ensure disabled state wins */
  &:disabled {
    border-color: #d9d9d9 !important;
    background: #f5f5f5 !important;
    color: #d9d9d9 !important;
    cursor: not-allowed;
  }
}

// Modal Confirm Styles
.modal-confirm {
  padding: 8px 0;

  .modal-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 12px 0;
  }

  .modal-description {
    font-size: 14px;
    color: #595959;
    line-height: 1.6;
    margin: 0 0 24px 0;
  }

  .form-item-revisi {
    margin-bottom: 24px;

    :deep(.ant-form-item-label > label) {
      font-weight: 600;
      color: #262626;
      font-size: 14px;

      &.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
        color: #ff4d4f;
      }
    }

    :deep(.ant-input) {
      border-radius: 6px;
    }

    :deep(.ant-input::placeholder) {
      color: #bfbfbf;
    }
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    @media (max-width: 768px) {
      flex-direction: column-reverse;
      gap: 8px;
    }

    .btn-cancel {
      border: 2px solid #1a1a1a;
      color: #1a1a1a;
      background: white;
      border-radius: 50px;
      font-weight: 600;
      height: 40px;
      padding: 0 24px;
      font-size: 14px;
      width: 100%;
      &:hover:not(:disabled) {
        background: #1a1a1a;
        color: white;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .btn-confirm {
      background: #e10f28;
      border: 2px solid #e10f28;
      color: white;
      border-radius: 50px;
      font-weight: 600;
      height: 40px;
      padding: 0 24px;
      font-size: 14px;
      width: 100%;

      &:hover:not(:disabled) {
        background: #c20d23;
        border-color: #c20d23;
      }

      &:disabled {
        background: #98a2b3;
        border-color: #98a2b3;
        cursor: not-allowed;
      }
    }
  }
}

::v-deep(.ant-tag) {
  font-weight: 700;
  border-radius: 50px;
}

:deep(.ant-form-item-label > label) {
  font-weight: 600;
  color: #262626;
}
</style>
